import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Tour IDs
export const TOUR_IDS = {
  DASHBOARD: 'dashboard',
  STOCK_DETAIL: 'stock_detail',
};

// Storage key for pending continuation
const PENDING_TOUR_KEY = 'stm_pending_tour';

export const TourProvider = ({ children }) => {
  const { token, user, isAuthenticated } = useAuth();
  
  // Current active tour
  const [activeTour, setActiveTour] = useState(null);
  const [tourRunning, setTourRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  
  // Cross-page navigation support
  // pendingContinue: indicates we're waiting for a route change to continue the tour
  const [pendingContinue, setPendingContinue] = useState(false);
  const [pendingTourId, setPendingTourId] = useState(null);
  const [pendingStepIndex, setPendingStepIndex] = useState(0);
  const [pendingRoute, setPendingRoute] = useState(null);
  
  // Track if we're in "click-to-continue" mode (overlay allows clicks)
  const [allowInteraction, setAllowInteraction] = useState(false);
  
  // Completed tours tracking
  const [completedTours, setCompletedTours] = useState(() => {
    const stored = localStorage.getItem('stm_completed_tours');
    return stored ? JSON.parse(stored) : [];
  });
  
  // Check for pending tour on mount (handles page refresh during tour)
  useEffect(() => {
    const pendingData = localStorage.getItem(PENDING_TOUR_KEY);
    if (pendingData) {
      try {
        const { tourId, stepIndex: savedStepIndex, route } = JSON.parse(pendingData);
        setPendingContinue(true);
        setPendingTourId(tourId);
        setPendingStepIndex(savedStepIndex);
        setPendingRoute(route);
      } catch (e) {
        localStorage.removeItem(PENDING_TOUR_KEY);
      }
    }
  }, []);
  
  // Sync completed tours from user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.tutorials_completed) {
      setCompletedTours(user.tutorials_completed);
    }
  }, [isAuthenticated, user?.tutorials_completed]);

  // Check if a tour has been completed
  const isTourCompleted = useCallback((tourId) => {
    return completedTours.includes(tourId);
  }, [completedTours]);

  // Mark tour as completed
  const markTourCompleted = useCallback(async (tourId) => {
    const newCompleted = [...new Set([...completedTours, tourId])];
    setCompletedTours(newCompleted);
    localStorage.setItem('stm_completed_tours', JSON.stringify(newCompleted));
    
    // Clear any pending continuation
    localStorage.removeItem(PENDING_TOUR_KEY);
    setPendingContinue(false);
    
    if (isAuthenticated && token) {
      try {
        await axios.post(
          `${API_BASE}/api/auth/tutorials/complete`,
          { tour_id: tourId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to persist tour completion:', error);
      }
    }
  }, [completedTours, isAuthenticated, token]);

  // Reset a specific tour (allow re-run)
  const resetTour = useCallback(async (tourId) => {
    const newCompleted = completedTours.filter(id => id !== tourId);
    setCompletedTours(newCompleted);
    localStorage.setItem('stm_completed_tours', JSON.stringify(newCompleted));
    
    if (isAuthenticated && token) {
      try {
        await axios.post(
          `${API_BASE}/api/auth/tutorials/reset`,
          { tour_id: tourId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to reset tour:', error);
      }
    }
  }, [completedTours, isAuthenticated, token]);

  // Reset all tours
  const resetAllTours = useCallback(async () => {
    setCompletedTours([]);
    localStorage.setItem('stm_completed_tours', JSON.stringify([]));
    localStorage.removeItem(PENDING_TOUR_KEY);
    
    if (isAuthenticated && token) {
      try {
        await axios.post(
          `${API_BASE}/api/auth/tutorials/reset-all`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to reset all tours:', error);
      }
    }
  }, [isAuthenticated, token]);

  // Start a tour
  const startTour = useCallback((tourId, startStepIndex = 0) => {
    setActiveTour(tourId);
    setStepIndex(startStepIndex);
    setTourRunning(true);
    setAllowInteraction(false);
    setPendingContinue(false);
    localStorage.removeItem(PENDING_TOUR_KEY);
  }, []);

  // Stop the current tour
  const stopTour = useCallback(() => {
    setTourRunning(false);
    setActiveTour(null);
    setStepIndex(0);
    setAllowInteraction(false);
    setPendingContinue(false);
    localStorage.removeItem(PENDING_TOUR_KEY);
  }, []);

  /**
   * Prepare for cross-page tour continuation.
   * Call this before navigating to a new page where the tour should continue.
   * @param {string} nextTourId - The tour to continue (e.g., TOUR_IDS.STOCK_DETAIL)
   * @param {number} nextStepIndex - The step index to start at on the new page
   * @param {string} expectedRoute - Route pattern to match (e.g., '/app/stock/')
   */
  const prepareForNavigation = useCallback((nextTourId, nextStepIndex, expectedRoute) => {
    // Pause current tour
    setTourRunning(false);
    
    // Store pending state for after navigation
    const pendingData = {
      tourId: nextTourId,
      stepIndex: nextStepIndex,
      route: expectedRoute,
      timestamp: Date.now()
    };
    localStorage.setItem(PENDING_TOUR_KEY, JSON.stringify(pendingData));
    
    setPendingContinue(true);
    setPendingTourId(nextTourId);
    setPendingStepIndex(nextStepIndex);
    setPendingRoute(expectedRoute);
  }, []);

  /**
   * Resume tour after navigation. Call this from the destination page
   * when the required elements are ready.
   * @returns {boolean} - True if tour was resumed, false if no pending tour
   */
  const resumeAfterNavigation = useCallback(() => {
    if (!pendingContinue || !pendingTourId) {
      return false;
    }
    
    // Clear pending state
    localStorage.removeItem(PENDING_TOUR_KEY);
    setPendingContinue(false);
    
    // Start the tour at the pending step
    setActiveTour(pendingTourId);
    setStepIndex(pendingStepIndex);
    setTourRunning(true);
    setAllowInteraction(false);
    
    // Clear pending refs
    setPendingTourId(null);
    setPendingStepIndex(0);
    setPendingRoute(null);
    
    return true;
  }, [pendingContinue, pendingTourId, pendingStepIndex]);

  /**
   * Cancel any pending tour continuation (e.g., if user navigates elsewhere)
   */
  const cancelPendingTour = useCallback(() => {
    localStorage.removeItem(PENDING_TOUR_KEY);
    setPendingContinue(false);
    setPendingTourId(null);
    setPendingStepIndex(0);
    setPendingRoute(null);
  }, []);

  // Handle tour callback - simplified for custom implementation
  const handleTourCallback = useCallback((data) => {
    // This is kept for compatibility but our custom implementation
    // handles navigation directly in AppTour component
  }, []);

  const value = {
    // State
    activeTour,
    tourRunning,
    stepIndex,
    completedTours,
    pendingContinue,
    pendingTourId,
    pendingRoute,
    allowInteraction,
    
    // Actions
    startTour,
    stopTour,
    isTourCompleted,
    markTourCompleted,
    resetTour,
    resetAllTours,
    handleTourCallback,
    setStepIndex,
    setAllowInteraction,
    prepareForNavigation,
    resumeAfterNavigation,
    cancelPendingTour,
    
    // Direct setters (for AppTour internal use)
    setActiveTour,
    setTourRunning,
    setPendingContinue,
    setPendingTourId,
    setPendingRoute,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
